"use client";

import { useSelector } from "@legendapp/state/react";
import { CheckCircle, Loader2, Wand2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import {
  applyImprovements,
  getThumbnailReviewerState,
  Results,
  setThumbnailReviewerState,
  STEP,
  thumbnailReviewerState,
} from "./state";

const BoldText = ({ text }: { text: string }) => {
  // Split the text by ** markers and map each part
  const parts = text.split(/(\*\*.*?\*\*)/g);

  return (
    <span>
      {parts.map((part, index) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          // Remove ** markers and wrap in strong tag
          return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        return part;
      })}
    </span>
  );
};

const CustomCommandsSection = ({
  customCommands,
  setCustomCommands,
  commonCommands,
  addCommonCommand,
}: {
  customCommands: string | null;
  setCustomCommands: (commands: string) => void;
  commonCommands: string[];
  addCommonCommand: (command: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">{t("thumbnailReviewer.results.customCommands.title")}</h3>
      <textarea
        className="min-h-[120px] w-full rounded-md border p-3"
        value={customCommands || ""}
        onChange={(e) => setCustomCommands(e.target.value)}
        placeholder={t("thumbnailReviewer.results.customCommands.placeholder")}
      />

      <div>
        <p className="mb-2 text-sm text-muted-foreground">
          {t("thumbnailReviewer.results.customCommands.commonCommandsLabel")}
        </p>
        <div className="flex flex-wrap gap-2">
          {commonCommands.map((command, index) => (
            <Button
              key={index}
              variant="outline"
              size="sm"
              onClick={() => addCommonCommand(command)}
            >
              {command}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export function ResultsSection() {
  const { t } = useTranslation();

  const results = useSelector(thumbnailReviewerState.results) as Results | null;
  const improvedImage = useSelector(thumbnailReviewerState.improvedImage);
  const isLoading = useSelector(thumbnailReviewerState.isLoading);
  const improvements = useSelector(thumbnailReviewerState.improvements);
  const step = useSelector(thumbnailReviewerState.step);
  const isDescriptionLoading = isLoading && step === STEP.DESCRIPTION;
  const isGettingImprovements = isLoading && step === STEP.RESULTS;
  const selectedImprovements = useSelector(thumbnailReviewerState.selectedImprovements);
  const customCommands = useSelector(getThumbnailReviewerState.customCommands);
  const [showCustomCommands, setShowCustomCommands] = useState(false);

  const toggleImprovement = (index: number) => {
    // Update the selected improvements array
    const newSelected = [...selectedImprovements].map((x) => x ?? false);
    newSelected[index] = !newSelected[index];
    setThumbnailReviewerState.selectedImprovements(newSelected);

    // Get the command for the toggled improvement
    const command = improvements?.[index]?.command;
    if (!command) return;

    // Current commands as array for easier manipulation
    const currentCommands = (customCommands || "").split("\n").filter((cmd) => cmd.trim() !== "");

    if (newSelected[index]) {
      // If selected, add the command if it doesn't already exist
      if (!currentCommands.includes(command)) {
        const newCommands = [...currentCommands, command].join("\n");
        setThumbnailReviewerState.customCommands(newCommands);
      }
    } else {
      // If unselected, remove the command if it exists
      const commandIndex = currentCommands.indexOf(command);
      if (commandIndex !== -1) {
        currentCommands.splice(commandIndex, 1);
        setThumbnailReviewerState.customCommands(currentCommands.join("\n"));
      }
    }
  };

  const addCommonCommand = (command: string) => {
    const currentCommands = customCommands || "";
    const newCommands = currentCommands ? `${currentCommands}\n${command}` : command;
    setThumbnailReviewerState.customCommands(newCommands);
  };

  const generateImprovedThumbnail = async () => {
    await applyImprovements(customCommands);
  };

  const resizeToYouTubeDimensions = (imageData: string, quality = 1.0): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        // Determine if landscape or portrait orientation
        const isLandscape = img.width >= img.height;
        const targetWidth = isLandscape ? 1280 : 720;
        const targetHeight = isLandscape ? 720 : 1280;

        // Create canvas for resizing
        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");

        // Draw image on canvas with new dimensions
        ctx?.drawImage(img, 0, 0, targetWidth, targetHeight);

        // Convert canvas to blob with JPEG format and quality setting
        canvas.toBlob(
          (resizedBlob) => {
            if (!resizedBlob) {
              reject(new Error("Failed to create blob from canvas"));
              return;
            }
            resolve(resizedBlob);
          },
          "image/jpeg",
          quality
        ); // Changed from PNG to JPEG with quality parameter
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      // Set source of image
      img.src = imageData;
    });
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.target = "_blank"; // Open in new tab/prevent navigation
    link.rel = "noopener noreferrer"; // Security best practice

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const base64ToBlob = (base64Data: string, mimeType: string): Blob => {
    const byteCharacters = atob(base64Data.split(",")[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  const downloadImprovedThumbnail = async () => {
    if (!improvedImage) return;

    try {
      // Resize the image to YouTube dimensions and compress as JPEG
      const resizedBlob = await resizeToYouTubeDimensions(improvedImage);
      downloadBlob(resizedBlob, "improved-thumbnail.jpg");
    } catch (error) {
      console.error("Error downloading thumbnail:", error);

      // Fallback to original image if resizing fails
      const originalBlob = base64ToBlob(improvedImage, "image/jpeg");
      downloadBlob(originalBlob, "improved-thumbnail.jpg");
    }
  };

  const backToSuggestions = () => {
    setThumbnailReviewerState.step(STEP.RESULTS);
    setThumbnailReviewerState.improvedImage(null);
  };

  // Common improvement commands
  const commonCommands = [
    "Add a white border to the character",
    "Add glow effect around character",
    "Change background image to darker tone",
    "Add a contrast border to the text",
    "Make text larger and more visible",
    "Add dramatic lighting to the scene",
    "Increase color saturation",
    "Add subtle vignette effect",
  ];

  if (!results) return null;

  if (isDescriptionLoading) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">{t("thumbnailReviewer.upload.analyzing")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!improvedImage ? (
        <>
          <div className="space-y-4">
            <h3 className="text-lg font-medium">
              {t("thumbnailReviewer.results.improvements.title")}
            </h3>
            <ul className="space-y-3">
              {improvements?.map((improvement, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Checkbox
                    checked={selectedImprovements[index]}
                    onCheckedChange={() => toggleImprovement(index)}
                    className="mt-1"
                  />
                  <BoldText text={improvement?.explanation ?? ""} />
                </li>
              ))}
            </ul>
          </div>

          <div className="relative flex items-center py-2">
            <div className="flex-grow border-t border-gray-200"></div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCustomCommands(!showCustomCommands)}
              className="mx-4 flex items-center gap-2"
            >
              {showCustomCommands
                ? t("thumbnailReviewer.results.customCommands.hide")
                : t("thumbnailReviewer.results.customCommands.show")}
              <span className="text-xs">{showCustomCommands ? "▲" : "▼"}</span>
            </Button>
            <div className="flex-grow border-t border-gray-200"></div>
          </div>

          {showCustomCommands && (
            <CustomCommandsSection
              customCommands={customCommands}
              setCustomCommands={(value) => setThumbnailReviewerState.customCommands(value)}
              commonCommands={commonCommands}
              addCommonCommand={addCommonCommand}
            />
          )}

          <div className="flex justify-center pt-4">
            <Button
              onClick={generateImprovedThumbnail}
              className="gap-2"
              disabled={isGettingImprovements}
            >
              {isGettingImprovements ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                  {t("thumbnailReviewer.results.gettingImprovements")}
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4" /> {t("thumbnailReviewer.results.applyImprovements")}
                </>
              )}
            </Button>
          </div>
          <p className="px-4 text-center text-sm text-muted-foreground">
            {t("thumbnailReviewer.results.improvementNote")}
          </p>
        </>
      ) : (
        <>
          <div className="space-y-4">
            <h3 className="text-lg font-medium">
              {t("thumbnailReviewer.results.appliedImprovements")}
            </h3>
            <ul className="space-y-3">
              {customCommands
                ?.split("\n")
                .filter((cmd) => cmd.trim() !== "")
                .map((improvement, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <BoldText text={improvement ?? ""} />
                  </li>
                ))}
            </ul>
          </div>

          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Button variant="outline" onClick={backToSuggestions}>
              {t("thumbnailReviewer.results.actions.backToSuggestions")}
            </Button>
            <Button onClick={downloadImprovedThumbnail}>
              {t("thumbnailReviewer.results.actions.download")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
